/**
 * Notification feed (bell) on the ms-agents backend.
 *
 * Each function takes the ms-agents verb (imported from `commsApi.js`) as its
 * first argument. `get` returns `response.data`; `post` returns `{ data, status }`.
 *
 * Feed response: `{ events: [{ id, event_type, room_id, message_id,
 * broadcast_id, payload, read_at, created_at }], unread_count, limit, offset }`.
 */
import { buildQuery } from "./_query";
import { wrapCommsError } from "./_error";

export const FEED_LIMIT = 50;

/**
 * @param {Function} get - ms-agents GET verb.
 * @param {{ limit?: number, unreadOnly?: boolean }} [options]
 * @returns {Promise<{ events: Array<Object>, unread_count: number }>}
 */
export const getNotificationFeed = async (get, { limit = FEED_LIMIT, unreadOnly } = {}) => {
  try {
    return await get(
      `/api/v1/notifications/feed${buildQuery({ limit, unread_only: unreadOnly })}`
    );
  } catch (err) {
    throw wrapCommsError(err, "Couldn't load notifications");
  }
};

/**
 * @param {Function} post - ms-agents POST verb.
 * @param {string} eventId
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const markNotificationRead = async (post, eventId) => {
  try {
    return await post(
      `/api/v1/notifications/feed/${encodeURIComponent(eventId)}/read`,
      {}
    );
  } catch (err) {
    throw wrapCommsError(err, "Couldn't mark the notification read");
  }
};

/**
 * @param {Function} post - ms-agents POST verb.
 * @returns {Promise<{ data: { marked_read: number }, status: number }>}
 */
export const markAllNotificationsRead = async (post) => {
  try {
    return await post("/api/v1/notifications/feed/read-all", {});
  } catch (err) {
    throw wrapCommsError(err, "Couldn't mark notifications read");
  }
};
